import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import {
	useFetchTrendInsightCountMutation,
	useFetchTrendInsightsMutation
} from '@features/trend-research/api/onboardingApi';

import {
	useExploreProductsSource,
	useSelectedGroup
} from '@store/reducers/trend-research/TrendResearchReducer';

import { isPageReachBottom } from '@utils/Util';

const useKeyDesigns = (keyType, requestBody, summaryField) => {
	const [designs, setDesigns] = useState([]);
	const selectedGroup = useSelectedGroup();
	const sourceType = useExploreProductsSource();
	const [page, setPage] = useState(null);
	const [fetchTrendInsightCount, { isLoading: countLoading }] = useFetchTrendInsightCountMutation();
	const [selectedCount, setSelectedCount] = useState(null);
	let params = `?size=20&page=${page}`;

	const apiSegment =
		sourceType === 'BRAND' && selectedGroup === 'FABRIC' ? `brand-insight/${keyType}` : keyType;
	const [fetchTrendInsights, { data, isLoading }] = useFetchTrendInsightsMutation();

	const debouncedFetch = useDebouncedCallback(async () => {
		const data = await fetchTrendInsightCount({
			apiSegment: `${sourceType.toLowerCase()}-insight/count`,
			requestBody
		}).unwrap();
		setSelectedCount(data?.[summaryField]);
	}, 100);

	useEffect(() => {
		setSelectedCount(null);
		debouncedFetch();
	}, [requestBody, sourceType]);

	useEffect(() => {
		setPage(0);
	}, [keyType, requestBody, sourceType]);

	const debounce = useDebouncedCallback(() => {
		if (selectedCount !== null && page !== null) {
			fetchTrendInsights({
				apiSegment,
				queryParams: params,
				requestBody: { ...requestBody, [summaryField]: selectedCount }
			});
		}
	}, 10);

	useEffect(() => {
		if (requestBody && selectedCount !== null && page !== null) {
			debounce();
		}
	}, [requestBody, page, selectedCount, keyType]);

	useEffect(() => {
		if (data?.data?.length > 0) {
			if (data?.currentPage > 0) {
				setDesigns((prev) => [...prev, ...data.data]);
			} else {
				setDesigns(data.data);
			}
		}
		if (data?.currentPage === 0 && data?.data?.length === 0) {
			setDesigns([]);
		}
	}, [data]);

	const handleScroll = useDebouncedCallback(() => {
		if (isPageReachBottom() && !isLoading && data?.currentPage + 1 < data?.totalPages) {
			setPage((prev) => prev + 1);
		}
	}, 300);

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, [keyType, handleScroll]);

	return { designs, isLoading: countLoading || isLoading };
};

export default useKeyDesigns;
